// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { Briefcase, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useRecentOpenedOpportunities } from "@/hooks/api/use-recent-opened-opportunities";
import { formatDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";

interface RecentOpenedOpportunitiesProps {
  days?: number;
  limit?: number;
  className?: string;
}

export function RecentOpenedOpportunities({
  days = 90,
  limit = 5,
  className,
}: RecentOpenedOpportunitiesProps) {
  const navigate = useNavigate();
  const {
    data: opportunities,
    isLoading,
    error,
  } = useRecentOpenedOpportunities(days, limit);

  const handleRowClick = (
    event: React.MouseEvent,
    accountId: string,
    opportunityId: string,
  ) => {
    const url = `/accounts/${accountId}/explorer?opportunityId=${opportunityId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <Card
      className={cn("w-full", className)}
      data-testid="recent-opened-opportunities-card"
    >
      <CardHeader className="flex flex-row items-center gap-x-3 pb-2">
        <Briefcase className="h-9 w-9 text-primary flex-shrink-0" />
        <div className="space-y-1">
          <CardTitle className="text-md">
            <span>Recent new logo opportunities</span>
          </CardTitle>
          <CardDescription>
            Selection of new business opportunities that were recently opened
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="flex justify-between">
                  <Skeleton className="h-5 w-[250px]" />
                  <Skeleton className="h-5 w-[100px]" />
                </div>
              ))}
          </div>
        ) : error ? (
          <div className="text-sm text-muted-foreground py-2">
            Error loading recently opened opportunities.
          </div>
        ) : !opportunities?.length ? (
          <div className="text-sm text-muted-foreground py-2">
            No recently opened opportunities found.
          </div>
        ) : (
          <div className="space-y-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-3/4">Account / Opportunity</TableHead>
                  <TableHead className="text-right whitespace-nowrap w-1/4">
                    Created Date
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.map((opp) => (
                  <Tooltip key={opp.opportunity_id}>
                    <TooltipTrigger asChild>
                      <TableRow
                        className="cursor-pointer hover:bg-accent group"
                        onClick={(e) =>
                          handleRowClick(e, opp.account_id, opp.opportunity_id)
                        }
                        data-testid={`opportunity-row-${opp.opportunity_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-x-4">
                            <div>
                              <div className="font-medium">
                                {opp.account_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                <span>{opp.opportunity_name}</span>
                              </div>
                            </div>

                            {opp.hasPublishedMilestoneAnalysis && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Link
                                    to={`/accounts/${opp.account_id}/milestone-analyses?opportunityId=${opp.opportunity_id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="gap-2 bg-card border-warning text-warning hover:bg-warning hover:text-warning-foreground shrink-0"
                                      data-testid={`milestone-analysis-btn-${opp.opportunity_id}`}
                                    >
                                      <Sparkles className="h-3 w-3" />
                                      <span>Milestone Analysis</span>
                                    </Button>
                                  </Link>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <p>View Milestone Analysis</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatDate(opp.created_date)}
                        </TableCell>
                      </TableRow>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View opportunity in account explorer</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
